import React, { useState, useContext, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import useCartStore from "./store/shoppingCardStore";
import styles from "./styles/productPage.module.css";
import backarrow from "./images/back arrow.png";
import { FiPlus, FiMinus, FiShoppingCart } from "react-icons/fi";
import { Helmet } from "react-helmet";
import { ProductsContext } from "./context/ProductContextProvider";
import { slugify } from "./utils/utils";
import CallUsFloatingButton from "./components/CallUsFloatingButton";

const Product = () => {
  const { productSlug } = useParams();
  const { products } = useContext(ProductsContext);
  const navigate = useNavigate();

  const add = useCartStore((state) => state.addItem);
  const remove = useCartStore((state) => state.removeItem);
  const { itemscount } = useCartStore();

  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [prevProduct, setPrevProduct] = useState(null);
  const [nextProduct, setNextProduct] = useState(null);

  // with = با داغی
  // without = بدون داغی
  const [batteryOption, setBatteryOption] = useState("with");

  useEffect(() => {
    if (products && products.length > 0) {
      const foundIndex = products.findIndex(
        (p) => slugify(p.name) === productSlug,
      );

      if (foundIndex !== -1) {
        const foundProduct = products[foundIndex];

        setProduct(foundProduct);
        setActiveImage(0);

        setPrevProduct(foundIndex > 0 ? products[foundIndex - 1] : null);

        setNextProduct(
          foundIndex < products.length - 1 ? products[foundIndex + 1] : null,
        );

        // هر محصول که باز می‌شود، ابتدا با داغی باشد
        setBatteryOption("with");
      } else {
        navigate("/محصولات");
      }
    }
  }, [productSlug, products, navigate]);

  // ==============================
  // Loading
  // ==============================

  if (!product) {
    return (
      <>
        <Navbar />

        <div className={styles.loadingState}>در حال بارگذاری محصول...</div>

        <Footer />
      </>
    );
  }

  // ==============================
  // اطلاعات اصلی محصول
  // ==============================

  const isOutOfStock = product.name.includes("(ناموجود)");

  // تشخیص UPS
  const isUPSBattery =
    product.name.toLowerCase().includes("ups") ||
    product.name.includes("یو پی اس");

  // ==============================
  // استخراج آمپر
  // فقط برای باتری خودرویی استفاده می‌شود
  // ==============================

  const ampMatch = product.name.match(/(\d+)\s*آمپر/);

  const amp = ampMatch ? parseInt(ampMatch[1], 10) : 0;

  // ==============================
  // مبلغ داغی باتری خودرویی
  // ==============================

  const scrapPricePerAmp = 50000;

  // مبلغ اضافه برای باتری خودرویی
  const carScrapPrice = amp * scrapPricePerAmp;

  // ==============================
  // قیمت نهایی
  // ==============================

  let currentPrice = product.price;

  if (isUPSBattery) {
    // =================================
    // باتری UPS
    // قیمت‌ها کاملاً دستی هستند
    // =================================

    if (batteryOption === "with") {
      // قیمت با داغی
      currentPrice = product.price;
    } else {
      // قیمت بدون داغی
      // از priceWithoutScrap خوانده می‌شود
      currentPrice = product.priceWithoutScrap ?? product.price;
    }
  } else {
    // =================================
    // باتری خودرویی
    // =================================

    if (batteryOption === "with") {
      // قیمت با داغی
      currentPrice = product.price;
    } else {
      // قیمت بدون داغی
      currentPrice = product.price + carScrapPrice;
    }
  }

  // اطمینان از اینکه قیمت عدد است
  currentPrice = Number(currentPrice) || 0;

  // ==============================
  // قیمت بدون داغی برای نمایش
  // ==============================

  const withoutScrapPrice = isUPSBattery
    ? Number(product.priceWithoutScrap ?? product.price)
    : Number(product.price + carScrapPrice);

  // ==============================
  // عنوان گزینه بدون داغی
  // ==============================

  const withoutScrapText = isUPSBattery
    ? "بدون داغی"
    : `بدون داغی (+${carScrapPrice.toLocaleString()} تومان)`;

  return (
    <div className={styles.productPage}>
      {/* =========================================
          SEO
      ========================================= */}

      <Helmet>
        <title>{product.meta?.title || product.name} | باتری۱۰۰</title>

        <meta
          name="description"
          content={product.meta?.description || product.info || ""}
        />

        <meta property="og:title" content={product.name} />

        <meta property="og:description" content={product.info || ""} />

        <meta property="og:type" content="product" />

        <meta
          property="og:url"
          content={`https://battery100.ir/product/${productSlug}`}
        />

        {product.image && product.image[0] && (
          <meta property="og:image" content={product.image[0]} />
        )}

        {product.image && product.image[0] && (
          <meta name="product_image" content={product.image[0]} />
        )}

        {/* قیمت فعلی انتخاب‌شده */}
        <meta property="product:price:amount" content={currentPrice} />

        <meta property="product:price:currency" content="IRR" />

        <meta name="product_price" content={currentPrice} />

        <meta
          name="product_old_price"
          content={product.oldPrice || product.price || ""}
        />

        {/* اطلاعات ترب */}

        <meta name="product_id" content={product.id || ""} />

        <meta name="product_name" content={product.name || ""} />

        <meta
          name="availability"
          content={isOutOfStock ? "outofstock" : "instock"}
        />

        <meta
          name="guarantee"
          content={product.guarantee || "12 ماه گارانتی"}
        />

        {/* Schema */}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",

            name: product.name,

            image: product.image || [],

            description: product.info || "",

            sku: product.id,

            offers: {
              "@type": "Offer",

              url: `https://battery100.ir/product/${productSlug}`,

              priceCurrency: "IRR",

              price: currentPrice,

              itemCondition: "https://schema.org/NewCondition",

              availability: isOutOfStock
                ? "https://schema.org/OutOfStock"
                : "https://schema.org/InStock",
            },
          })}
        </script>
      </Helmet>

      {/* =========================================
          Navbar
      ========================================= */}

      <Navbar />

      <main className={styles.contentWrapper}>
        {/* =========================================
            Breadcrumb
        ========================================= */}

        <nav aria-label="breadcrumb" className={styles.breadcrumb}>
          <ol>
            <li>
              <Link to="/">خانه</Link>
            </li>

            <li>
              <Link to="/محصولات/">محصولات</Link>
            </li>

            <li aria-current="page">{product.name}</li>
          </ol>
        </nav>

        {/* =========================================
            Back Button
        ========================================= */}

        <Link to="/محصولات/" className={styles.backButton}>
          <img
            src={backarrow}
            alt="بازگشت به صفحه محصولات"
            title="بازگشت به لیست محصولات"
            className={styles.backarrow}
            width="20"
            height="20"
          />

          <span>بازگشت به محصولات</span>
        </Link>

        {/* =========================================
            Product Container
        ========================================= */}

        <div className={styles.productContainer}>
          {/* =====================================
              Gallery
          ===================================== */}

          <div className={styles.productGallery}>
            <figure>
              {product.image && product.image.length > 0 && (
                <img
                  src={product.image[activeImage]}
                  alt={`${product.name} - تصویر اصلی`}
                  title={`مشخصات ${product.name}`}
                  className={styles.mainImage}
                  loading="lazy"
                  width="500"
                  height="500"
                />
              )}
            </figure>

            {/* Thumbnails */}

            {product.image && product.image.length > 1 && (
              <div className={styles.thumbnailContainer} role="tablist">
                {product.image.map((img, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`
                          ${styles.thumbnailWrapper}
                          ${index === activeImage ? styles.active : ""}
                        `}
                    onClick={() => setActiveImage(index)}
                    aria-label={`نمایش تصویر ${index + 1} از ${product.name}`}
                    role="tab"
                    aria-selected={index === activeImage}
                  >
                    <img
                      src={img}
                      alt={`${product.name} - تصویر ${index + 1}`}
                      className={styles.thumbnail}
                      loading="lazy"
                      width="100"
                      height="100"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* =====================================
              Product Details
          ===================================== */}

          <article className={styles.productDetails}>
            <header>
              {/* عنوان */}

              <h1 className={styles.productTitle}>{product.name}</h1>

              {/* دسته‌بندی */}

              <p className={styles.productCategory}>
                دسته‌بندی: {isUPSBattery ? "باتری یو پی اس" : "باتری ماشین"}
              </p>

              {/* =================================
                  Price
              ================================= */}

              <div className={styles.priceSection}>
                <span className={styles.priceLabel}>قیمت:</span>

                <span className={styles.priceValue}>
                  {currentPrice.toLocaleString()} تومان
                </span>
              </div>

              {/* =================================
                  Battery Options
              ================================= */}

              <div className={styles.batteryOptions}>
                <h3>نوع خرید</h3>

                {/* =============================
                    با داغی
                ============================== */}

                <label
                  className={`
                    ${styles.optionCard}
                    ${batteryOption === "with" ? styles.active : ""}
                  `}
                >
                  <div className={styles.optionLeft}>
                    <input
                      type="radio"
                      name="batteryOption"
                      checked={batteryOption === "with"}
                      onChange={() => setBatteryOption("with")}
                    />

                    <div>
                      <div className={styles.optionTitle}>با داغی</div>

                      <div className={styles.optionPrice}>
                        تحویل باتری فرسوده
                      </div>
                    </div>
                  </div>

                  <div className={styles.optionRight}>
                    {Number(product.price).toLocaleString()} تومان
                  </div>
                </label>

                {/* =============================
                    بدون داغی
                ============================== */}

                <label
                  className={`
                    ${styles.optionCard}
                    ${batteryOption === "without" ? styles.active : ""}
                  `}
                >
                  <div className={styles.optionLeft}>
                    <input
                      type="radio"
                      name="batteryOption"
                      checked={batteryOption === "without"}
                      onChange={() => setBatteryOption("without")}
                    />

                    <div>
                      <div className={styles.optionTitle}>
                        {withoutScrapText}
                      </div>

                      <div className={styles.optionPrice}>
                        {isUPSBattery ? "" : "بدون تحویل باتری فرسوده"}
                      </div>
                    </div>
                  </div>

                  <div className={styles.optionRight}>
                    {withoutScrapPrice.toLocaleString()} تومان
                  </div>
                </label>
              </div>
            </header>

            {/* =================================
                Description
            ================================= */}

            <section
              className={styles.productDescription}
              aria-labelledby="product-desc-heading"
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: product.info || "",
                }}
              />
            </section>

            {/* =================================
                Technical Specs
            ================================= */}

            <section
              className={styles.productFeatures}
              aria-labelledby="product-specs-heading"
            >
              <h2 id="product-specs-heading">ویژگی‌های فنی {product.name}</h2>

              <div
                className={styles.productSpecs}
                dangerouslySetInnerHTML={{
                  __html:
                    product.infos ||
                    `
                      <ul>
                        <li>
                          <strong>نوع:</strong>
                          اتمی سیلد (خشک)
                        </li>

                        <li>
                          <strong>آمپر ساعت:</strong>
                          60Ah
                        </li>

                        <li>
                          <strong>ولتاژ:</strong>
                          12 ولت
                        </li>

                        <li>
                          <strong>ابعاد:</strong>
                          227×175×242 میلی‌متر
                        </li>

                        <li>
                          <strong>وزن:</strong>
                          16.9 کیلوگرم
                        </li>

                        <li>
                          <strong>گارانتی:</strong>
                          12 ماه
                        </li>

                        <li>
                          <strong>سازنده:</strong>
                          شرکت صبا باتری
                        </li>
                      </ul>
                    `,
                }}
              />
            </section>

            {/* =================================
                Cart Actions
            ================================= */}

            <div className={styles.actions}>
              {/* ناموجود */}

              {isOutOfStock ? (
                <button className={styles.outOfStockButton} disabled>
                  ناموجود
                </button>
              ) : itemscount[product.id] > 0 ? (
                <>
                  {/* Quantity */}

                  <div className={styles.quantityControl}>
                    <button
                      type="button"
                      onClick={() => remove(product.id)}
                      className={styles.quantityButton}
                      aria-label="کاهش تعداد"
                    >
                      <FiMinus />
                    </button>

                    <span className={styles.quantityValue}>
                      {itemscount[product.id]}
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        add({
                          ...product,
                          price: currentPrice,
                          batteryOption: batteryOption,
                        })
                      }
                      className={styles.quantityButton}
                      aria-label="افزایش تعداد"
                    >
                      <FiPlus />
                    </button>
                  </div>

                  {/* Total Price */}

                  <div className={styles.totalPrice}>
                    <span>مبلغ قابل پرداخت:</span>

                    <span className={styles.totalValue}>
                      {(currentPrice * itemscount[product.id]).toLocaleString()}{" "}
                      تومان
                    </span>
                  </div>

                  {/* Checkout */}

                  <Link to="/سبد خرید/" className={styles.checkoutButton}>
                    <FiShoppingCart />

                    <span>تکمیل خرید</span>
                  </Link>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    add({
                      ...product,
                      price: currentPrice,
                      batteryOption: batteryOption,
                    })
                  }
                  className={styles.addToCartButton}
                  aria-label={`افزودن ${product.name} به سبد خرید`}
                >
                  <FiShoppingCart />

                  <span>افزودن به سبد خرید</span>
                </button>
              )}
            </div>
          </article>
        </div>

        {/* =========================================
            Suggestions
        ========================================= */}

        <section className={styles.suggestionSection}>
          <h2>محصولات پیشنهادی</h2>

          <div className={styles.suggestionCardsContainer}>
            {/* Previous */}

            {prevProduct && (
              <Link
                to={`/product/${slugify(prevProduct.name)}`}
                className={styles.suggestionCard}
              >
                {prevProduct.image && prevProduct.image[0] && (
                  <img
                    src={prevProduct.image[0]}
                    alt={prevProduct.name}
                    className={styles.suggestionImage}
                    loading="lazy"
                  />
                )}

                <div className={styles.suggestionText}>
                  <h3>{prevProduct.name}</h3>

                  <span className={styles.suggestionPrice}>
                    {Number(prevProduct.price).toLocaleString()} تومان
                  </span>
                </div>
              </Link>
            )}

            {/* Next */}

            {nextProduct && (
              <Link
                to={`/product/${slugify(nextProduct.name)}`}
                className={styles.suggestionCard}
              >
                {nextProduct.image && nextProduct.image[0] && (
                  <img
                    src={nextProduct.image[0]}
                    alt={nextProduct.name}
                    className={styles.suggestionImage}
                    loading="lazy"
                  />
                )}

                <div className={styles.suggestionText}>
                  <h3>{nextProduct.name}</h3>

                  <span className={styles.suggestionPrice}>
                    {Number(nextProduct.price).toLocaleString()} تومان
                  </span>
                </div>
              </Link>
            )}

            {/* No Suggestions */}

            {!prevProduct && !nextProduct && (
              <p className={styles.noSuggestions}>
                محصول دیگری برای پیشنهاد وجود ندارد.
              </p>
            )}
          </div>
        </section>
      </main>

      {/* =========================================
          Footer
      ========================================= */}

      <Footer />

      {/* Floating Call Button */}

      <CallUsFloatingButton />
    </div>
  );
};

export default Product;
